import {
  addEdge,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import { useCallback, useState } from 'react';
import { saveVersion } from '../../lib/designer/versionHistory';
import Canvas from './Canvas';
import CostPanel from './CostPanel';
import HistoryPanel from './HistoryPanel';
import Palette from './Palette';
import PropertiesPanel from './PropertiesPanel';
import TerraformPreview from './TerraformPreview';
import Toolbar from './Toolbar';
import ValidationPanel from './ValidationPanel';
import TemplateGallery from './TemplateGallery';

const INITIAL_NODES = [];
const INITIAL_EDGES = [];

function DesignerShell() {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [designName, setDesignName] = useState('My Landing Zone');
  const [activeTab, setActiveTab] = useState('properties');
  const [showTemplates, setShowTemplates] = useState(false);

  const selectedNode = selectedNodeId
    ? nodes.find((n) => n.id === selectedNodeId) ?? null
    : null;

  const selectedNodeForPanel = selectedNode
    ? { id: selectedNode.id, data: selectedNode.data }
    : null;

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  const handleAddNode = useCallback(
    (node) => setNodes((nds) => [...nds, node]),
    [setNodes],
  );

  const handleNodeUpdate = useCallback(
    (nodeId, data) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n,
        ),
      );
    },
    [setNodes],
  );

  const handleNew = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setCurrentDesignId(null);
    setDesignName('My Landing Zone');
  }, [setNodes, setEdges]);

  const handleLoad = useCallback(
    (design) => {
      setNodes(
        design.nodes.map((n) => ({
          ...n,
          data: n.data,
        })),
      );
      setEdges(design.edges);
      setCurrentDesignId(design.id);
      setDesignName(design.name);
      setSelectedNodeId(null);
    },
    [setNodes, setEdges],
  );

  function handleApplyTemplate(template) {
    setNodes(template.nodes.map((n) => ({ ...n, data: { ...n.data } })));
    setEdges(template.edges.map((e) => ({ ...e })));
    setCurrentDesignId(null);
    setDesignName(template.name);
    setSelectedNodeId(null);
  }

  const tabBtnClass = (tab) => `flex-1 text-xs py-1.5 rounded-t transition-colors ${
    activeTab === tab
      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium'
      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
  }`;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-gray-900">
      <Toolbar
        nodes={nodes}
        edges={edges}
        designName={designName}
        currentDesignId={currentDesignId}
        onDesignNameChange={setDesignName}
        onNew={handleNew}
        onLoad={handleLoad}
        onSaved={(id) => {
          setCurrentDesignId(id);
          saveVersion(id, { name: designName, nodes, edges });
        }}
        onOpenTemplates={() => setShowTemplates(true)}
        onImportNodes={(importedNodes, importedEdges) => {
          setNodes(importedNodes);
          setEdges(importedEdges);
          setSelectedNodeId(null);
          setCurrentDesignId(null);
        }}
      />

      {showTemplates && (
        <TemplateGallery
          onApply={handleApplyTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-52 shrink-0 overflow-y-auto border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <Palette />
        </aside>

        <main className="flex-1 overflow-hidden">
          <ReactFlowProvider>
            <Canvas
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeSelect={setSelectedNodeId}
              onAddNode={handleAddNode}
            />
          </ReactFlowProvider>
        </main>

        <aside className="flex w-64 shrink-0 flex-col overflow-hidden border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex shrink-0 border-b border-gray-200 dark:border-gray-700 px-2 pt-2">
            <button className={tabBtnClass('properties')} onClick={() => setActiveTab('properties')}>
              Properties
            </button>
            <button className={tabBtnClass('terraform')} onClick={() => setActiveTab('terraform')}>
              Terraform
            </button>
            <button className={tabBtnClass('validation')} onClick={() => setActiveTab('validation')}>
              Validate
            </button>
            <button className={tabBtnClass('cost')} onClick={() => setActiveTab('cost')}>
              Cost
            </button>
            <button className={tabBtnClass('history')} onClick={() => setActiveTab('history')}>
              History
            </button>
          </div>

          {activeTab === 'properties' && (
            <div className="flex-1 overflow-y-auto">
              <PropertiesPanel
                selectedNode={selectedNodeForPanel}
                onUpdate={handleNodeUpdate}
              />
            </div>
          )}
          {activeTab === 'terraform' && (
            <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
              <TerraformPreview nodes={nodes} edges={edges} />
            </div>
          )}
          {activeTab === 'validation' && (
            <div className="flex-1 overflow-auto">
              <ValidationPanel nodes={nodes} edges={edges} />
            </div>
          )}
          {activeTab === 'cost' && (
            <div className="flex-1 overflow-auto">
              <CostPanel nodes={nodes} />
            </div>
          )}
          {activeTab === 'history' && (
            <div className="flex-1 overflow-auto">
              <HistoryPanel
                designId={currentDesignId}
                onLoadVersion={(v) => {
                  setNodes(v.nodes.map((n) => ({ ...n, data: n.data })));
                  setEdges(v.edges.map((e) => ({ ...e })));
                  setDesignName(v.name);
                  setSelectedNodeId(null);
                }}
              />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default DesignerShell;
